import React from "react"
import { Text, Flex } from "@capioweb/ui-components"
import {
	BooleanProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { QBankImage } from "../../@types/CapioWeb/core"
import {
	breakpoints,
	fontWeight,
	INNER_MAX_WIDTH,
	READABLE_LINE_MAX_WIDTH,
	scale,
} from "@capioweb/ui-components/src"
import { assetBaseUrl } from "../../utils"
import styled from "styled-components"

interface CapioQuoteBlockProps {
	imageAlignment: StringProperty
	imageBlock: QBankImage
	quoteName: StringProperty
	quoteText: StringProperty
	leftAligned: BooleanProperty
}

interface StyledBlockquoteProps {
	imgAlignment: string
	leftAligned: boolean
}

const StyledBlockquote = styled.div<StyledBlockquoteProps>`
	display: flex;
	flex-direction: ${(props) =>
		props.imgAlignment === "right" ? "row-reverse" : "row"};
	margin: 0;
	margin-left: ${(props) => (props.leftAligned ? 0 : "auto")};
	margin-right: ${(props) => (props.leftAligned ? 0 : "auto")};
	align-items: center;
	gap: ${scale.px(3)};
	max-width: ${scale.px(READABLE_LINE_MAX_WIDTH)};
	width: 100%;

	p {
		flex-grow: 1;
		background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI2LjMuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAzMC42OSAyNC45NyIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMzAuNjkgMjQuOTc7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsbDojMEQzMDUwO30KPC9zdHlsZT4KPHBhdGggY2xhc3M9InN0MCIgZD0iTTEzLjA5LDguMzZDMTMuMDksMi45Nyw5Ljc5LDAsNi4xNiwwYy0zLjMsMC01LjgzLDIuNzUtNS44Myw1Ljk0YzAsMy4zLDIuMiw1LjYxLDUuMjgsNS42MQoJYzAuNTUsMCwxLjMyLTAuMTEsMS41NC0wLjIyQzYuNzEsMTQuODUsMy41MiwxOS4yNSwwLDIxLjU2bDQuMDcsMy40MUM5LjEzLDIxLjM0LDEzLjA5LDE1LjA3LDEzLjA5LDguMzYgTTMwLjY5LDguMzYKCWMwLTUuMzktMy4zLTguMzYtNi45My04LjM2Yy0zLjE5LDAtNS44MywyLjc1LTUuODMsNS45NGMwLDMuMywyLjMxLDUuNjEsNS4zOSw1LjYxYzAuNDQsMCwxLjIxLTAuMTEsMS40My0wLjIyCgljLTAuNDQsMy41Mi0zLjUyLDcuOTItNy4wNCwxMC4yM2w0LjA3LDMuNDFDMjYuNzMsMjEuMzQsMzAuNjksMTUuMDcsMzAuNjksOC4zNiIvPgo8L3N2Zz4K");
		background-repeat: no-repeat;
		background-position: center 10px;
		background-size: ${scale.px(4)} auto;
		text-align: center;
		padding-top: ${scale.px(7)};
		max-width: ${scale.px(INNER_MAX_WIDTH / 2)};
		min-width: calc(50% - 12px);
		margin: auto;
		font-size: ${scale.px(3)};
		line-height: ${scale.px(4.5)};
		font-weight: ${fontWeight.bold};
	}

	img {
		min-width: calc(50% - 12px);
		aspect-ratio: 3/4;
		object-fit: cover;
		margin-bottom: 0;
	}

	@media screen and (max-width: ${scale.px(breakpoints.m / 8)}) {
		flex-direction: ${(props) =>
			props.imgAlignment === "right" ? "column-reverse" : "column"};

		gap: ${scale.px(4)};

		p {
			background-size: ${scale.px(3.125)} auto;
			padding-top: ${scale.px(5)};
		}
		img {
			min-width: 100%;
		}
	}
`

const CapioQuoteBlock: React.FC<CapioQuoteBlockProps> = (props) => {
	return (
		<Flex as="section" w={1} px={3} pb={3}>
			<Flex w={1} maxw={INNER_MAX_WIDTH} my={0} mx="auto">
				<StyledBlockquote
					imgAlignment={props.imageAlignment?.value.toLowerCase() ?? " left"}
					leftAligned={props.leftAligned.value}
				>
					{props.imageBlock?.media?.value && (
						<img
							src={`${assetBaseUrl()}${
								props.imageBlock?.media?.value?.url
							}?format=webp`}
							alt={props.imageBlock?.alternateText?.value}
							title={props.imageBlock?.imageTitle?.value}
						/>
					)}
					<Flex flexDirection="column" minw="calc(50% - 12px)" m="auto">
						<Text as="p">
							{props.quoteText?.value ?? ""}
							<Text
								as="span"
								display="block"
								mt={{ _: 1, m: 2 }}
								fontSize={1.75}
								lineHeight={2.5}
								fontWeight={fontWeight.normal}
							>
								{props.quoteName?.value ?? ""}
							</Text>
						</Text>
					</Flex>
				</StyledBlockquote>
			</Flex>
		</Flex>
	)
}

export { CapioQuoteBlock }
